// AGC: MiniKit Provider for Base App integration
// AGC: Wraps OnchainKit with MiniKit enabled for Base App mini apps
// AGC: CRITICAL: Uses OnchainKit native wallet components for Base App
// AGC: Shared across all FarmProHub apps

import React, { ReactNode, useEffect } from 'react'
import { OnchainKitProvider } from '@coinbase/onchainkit'
import { WagmiProvider, useAccount, useConnect } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit'
import { config, rainbowKitTheme } from '../config/wagmi-unified'
import { baseSepolia } from '../config/networks'
import { getAppMetadata } from '../config/app-metadata'
import type { ReactNode as OnchainKitReactNode } from 'react'

interface MiniKitProviderProps {
  children: ReactNode
}

const ONCHAINKIT_API_KEY = import.meta.env.VITE_ONCHAINKIT_API_KEY || ''

// AGC: Warn if API key is missing (MiniKit requires it)
if (!ONCHAINKIT_API_KEY) {
  console.warn('⚠️ AGC: VITE_ONCHAINKIT_API_KEY is missing - MiniKit may not work properly')
}

// AGC: Create React Query client for wagmi (separate instance for Base App)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      refetchOnWindowFocus: true,
    },
  },
})

/**
 * AGC: Base App Auto-Connect Component
 * AGC: Automatically connects the Base App embedded wallet when app loads
 * AGC: This ensures users don't see "Connecting Wallet..." forever
 */
const BaseAppAutoConnect: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isConnected, address } = useAccount()
  const { connectors, connectAsync } = useConnect()

  useEffect(() => {
    // AGC: Only auto-connect if not already connected
    if (isConnected) {
      console.log('✅ AGC: Base App wallet already connected:', address)
      return
    }

    // AGC: Find Coinbase Wallet connector (used by Base App)
    const coinbaseConnector = connectors.find(
      (c) => c.id === 'coinbaseWalletSDK' || c.name.toLowerCase().includes('coinbase')
    )

    if (!coinbaseConnector) {
      console.warn('⚠️ AGC: Coinbase Wallet connector not found in Base App')
      return
    }

    // AGC: Auto-connect to Base App embedded wallet
    console.log('🔌 AGC: Auto-connecting Base App embedded wallet...')
    connectAsync({ connector: coinbaseConnector })
      .then((result) => {
        console.log('✅ AGC: Base App wallet auto-connected!', result.accounts[0])
      })
      .catch((error) => {
        // AGC: Ignore "already connected" error - this is expected in React Strict Mode
        if (error?.name === 'ConnectorAlreadyConnectedError' || 
            error?.message?.includes('already connected')) {
          console.log('✅ AGC: Wallet already connected (expected in Strict Mode)')
          return
        }
        console.error('❌ AGC: Base App wallet auto-connect failed:', error)
      })
  }, [isConnected, address, connectors, connectAsync])

  return <>{children}</>
}

/**
 * AGC: MiniKit Provider Component
 * AGC: Provides full wagmi + RainbowKit + OnchainKit stack for Base App
 * AGC: CRITICAL: Must include RainbowKitProvider for wagmi transaction hooks
 */
export const MiniKitProvider: React.FC<MiniKitProviderProps> = ({ children }) => {
  const metadata = getAppMetadata()
  
  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={config} reconnectOnMount={true}>
        <RainbowKitProvider
          theme={darkTheme({
            ...rainbowKitTheme,
            colors: {
              accentColor: rainbowKitTheme.accentColor,
              accentColorForeground: rainbowKitTheme.accentColorForeground,
            },
          })}
          modalSize="compact"
          showRecentTransactions={true}
          initialChain={baseSepolia}
        >
          <OnchainKitProvider
            apiKey={ONCHAINKIT_API_KEY}
            chain={baseSepolia}
            config={{
              appearance: {
                name: metadata.name,
                logo: '/logo.png',
                mode: 'auto',
              },
              wallet: {
                display: 'modal',
              },
            }}
            miniKit={{
              enabled: Boolean(ONCHAINKIT_API_KEY),
            }}
          >
            <BaseAppAutoConnect>{children as OnchainKitReactNode}</BaseAppAutoConnect>
          </OnchainKitProvider>
        </RainbowKitProvider>
      </WagmiProvider>
    </QueryClientProvider>
  )
}

/**
 * AGC: Helper hook to check if OnchainKit API key is configured
 */
export const useOnchainKitConfigured = (): boolean => {
  return Boolean(ONCHAINKIT_API_KEY)
}

